import React, { useState } from 'react';

import {
  Space,
  Button,
  message,
  Tabs,
  Typography,
  Card,
  Input,
  DatePicker,
  Divider,
  Badge,
  Row,
  Col,
  Pagination,
} from 'antd';
import type { Dayjs } from 'dayjs';
import { RegisterConventionDocument } from '../../helper/apiHelper';
import { RegisteredConventionsTable } from './RegisteredConventionsTable';
import { usePendingConventions } from '../../hooks/BadgeHooks/LegalDepartmentHooks';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import styles from '../../Assets/Styles/Global.module.css';
import { useTranslation } from 'react-i18next';
import { formatDateUtc } from '../../Utils/date';

const LegalDepartmentComponent = (): React.ReactNode => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  // State for convention registration
  const [convRegNumber, setConvRegNumber] = useState<{ [key: number]: string }>(
    {}
  );
  const [convRegDate, setConvRegDate] = useState<{
    [key: number]: Dayjs | null;
  }>({});
  const [convSubmitting, setConvSubmitting] = useState<{
    [key: number]: boolean;
  }>({});

  const { data: pendingConventions = [] } = usePendingConventions();
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentConventions = pendingConventions.slice(startIndex, endIndex);

  const handleRegisterConvention = async (processId: number): Promise<void> => {
    const regNum = convRegNumber[processId];
    const regDate = convRegDate[processId];

    if (!regNum || !regDate) {
      messageApi.error(t('legDepComp.fillInBothFields'));
      return;
    }

    setConvSubmitting({ ...convSubmitting, [processId]: true });

    try {
      await RegisterConventionDocument(processId, {
        registrationNumber: Number(regNum),
        registrationDate: new Date(
          Date.UTC(regDate.year(), regDate.month(), regDate.date())
        ).toISOString(),
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PENDING_CONVENTIONS,
      });

      messageApi.success(t('legDepComp.conventionRegistrationSuccess'));

      setConvRegNumber({ ...convRegNumber, [processId]: '' });
      setConvRegDate({ ...convRegDate, [processId]: null });
    } catch (error) {
      console.error(error);
      messageApi.error(t('legDepComp.registrationFail'));
    } finally {
      setConvSubmitting({ ...convSubmitting, [processId]: false });
    }
  };

  return (
    <>
      {contextHolder}
      <Tabs centered type="card" defaultActiveKey="1">
        {/* NEW TAB: PENDING CONVENTION REGISTRATIONS */}
        <Tabs.TabPane
          tab={
            <span>
              {t('legDepComp.pendingConventions')}
              <Badge
                count={pendingConventions.length}
                overflowCount={10}
                size="small"
              />
            </span>
          }
          key="3"
        >
          <Typography.Title level={4}>
            {t('legDepComp.awaitRegistration')}
          </Typography.Title>
          <Divider />

          {pendingConventions.length === 0 ? (
            <Typography.Text>{t('legDepComp.noConventions')}</Typography.Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {currentConventions.map((process) => (
                <Card
                  key={process.id}
                  title={`${process.studentName} - ${process.organizationName}`}
                  extra={
                    <Typography.Link
                      href={`${process.conventionDocumentUrl}?status=${process.currentUniversityStepName}`}
                      target="_blank"
                    >
                      {t('legDepComp.viewPDF')}
                    </Typography.Link>
                  }
                >
                  <Row
                    justify="space-between"
                    gutter={[0, 4]}
                    style={{ width: '100%' }}
                  >
                    <Col xs={24} md={12}>
                      <Space direction="vertical" size={4}>
                        <Row>
                          <Typography.Text>
                            <strong>{t('legDepComp.student')}:</strong>{' '}
                            {process.studentName}
                          </Typography.Text>
                        </Row>

                        <Row>
                          <Typography.Text>
                            <strong>{t('legDepComp.group')}:</strong>{' '}
                            {process.studentGroup}
                          </Typography.Text>
                        </Row>

                        <Row>
                          <Typography.Text>
                            <strong>{t('legDepComp.tutor')}:</strong>{' '}
                            {process.tutorName || t('legDepComp.notAssigned')}
                          </Typography.Text>
                        </Row>

                        <Row>
                          <Typography.Text>
                            <strong>{t('legDepComp.supervisor')}:</strong>{' '}
                            {process.supervisorName ||
                              t('legDepComp.notAssigned')}
                          </Typography.Text>
                        </Row>
                      </Space>
                    </Col>

                    <Col xs={24} md={12}>
                      <Space direction="vertical" size={4}>
                        <Row>
                          <Typography.Text>
                            <strong>{t('legDepComp.organization')}:</strong>{' '}
                            {process.organizationName}
                          </Typography.Text>
                        </Row>

                        <Row>
                          <Typography.Text>
                            <strong>
                              {t('legDepComp.internshipStartDate')}:
                            </strong>{' '}
                            {formatDateUtc(process.internshipStartDate)}
                          </Typography.Text>
                        </Row>

                        <Row>
                          <Typography.Text>
                            <strong>
                              {t('legDepComp.internshipEndDate')}:
                            </strong>{' '}
                            {formatDateUtc(process.internshipEndDate)}
                          </Typography.Text>
                        </Row>
                      </Space>
                    </Col>

                    <Divider />

                    <Row style={{ width: '100%' }}>
                      <Col span={24}>
                        <Card
                          type="inner"
                          title={t('legDepComp.registerConvention')}
                          size="small"
                          style={{ backgroundColor: '#e6f7ff' }}
                        >
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Input
                              placeholder={t('legDepComp.regNumber')}
                              type="number"
                              value={convRegNumber[process.id] || ''}
                              onChange={(e) =>
                                setConvRegNumber({
                                  ...convRegNumber,
                                  [process.id]: e.target.value,
                                })
                              }
                              onWheel={(e) =>
                                (e.target as HTMLInputElement).blur()
                              }
                              className={styles.noSpinners}
                            />
                            <DatePicker
                              style={{ width: '100%' }}
                              placeholder={t('legDepComp.regDate')}
                              value={convRegDate[process.id]}
                              onChange={(date) =>
                                setConvRegDate({
                                  ...convRegDate,
                                  [process.id]: date,
                                })
                              }
                            />
                            <Button
                              type="primary"
                              block
                              loading={convSubmitting[process.id]}
                              disabled={
                                !convRegNumber[process.id] ||
                                !convRegDate[process.id]
                              }
                              onClick={() =>
                                handleRegisterConvention(process.id)
                              }
                            >
                              {t('legDepComp.register')}
                            </Button>
                          </Space>
                        </Card>
                      </Col>
                    </Row>
                  </Row>
                </Card>
              ))}

              {pendingConventions.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 16,
                  }}
                >
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={pendingConventions.length}
                    showSizeChanger={true}
                    pageSizeOptions={['1', '3', '5', '10', '15', '30', '50']}
                    onChange={(page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    }}
                  />
                </div>
              )}
            </Space>
          )}
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('legDepComp.registeredConventions')} tabKey="4">
          <RegisteredConventionsTable />
        </Tabs.TabPane>
      </Tabs>
      <br></br>
      <br></br>
    </>
  );
};
export default LegalDepartmentComponent;
